/**
 * O alto do palco: avisos de estado da sala. Quem está apresentando a tela (o outro lado, `presenter`; ou você, com o
 * botão de parar) e os erros dos recursos da câmera e da tela (compartilhar a tela, efeito de fundo). Tudo numa pilha
 * só, para dois avisos nunca ficarem um por cima do outro.
 */
import { useEffect } from 'react';
import { MonitorUp, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCallRecording } from '../recording/recordingContext';
import { useBackgroundEffect } from './BackgroundEffectProvider';
import { displayNameOf } from './participantDisplay';
import { useScreenShare } from './ScreenShareProvider';

/** O aviso de erro some sozinho; a pessoa também pode dispensá-lo. */
export const ERROR_VISIBLE_MS = 8000;

/** Pílula do alto do palco; na janela pequena (`compact`) ela é mais baixa, para cobrir menos da tela. */
function Pill({ children, className, compact, ...props }) {
    return (
        <div
            className={cn(
                'pointer-events-auto flex max-w-full items-center gap-2 rounded-full bg-neutral-900/90 text-white shadow-lg ring-1 ring-white/10',
                compact ? 'py-1 pl-3 pr-1 text-xs' : 'py-1.5 pl-3.5 pr-1.5 text-sm',
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
}

function ErrorNotice({ message, onDismiss, compact }) {
    useEffect(() => {
        const timer = setTimeout(onDismiss, ERROR_VISIBLE_MS);
        return () => clearTimeout(timer);
    }, [message, onDismiss]);
    return (
        <Pill role="alert" compact={compact} className="rounded-2xl bg-red-950/95 text-red-50 ring-red-400/40">
            <span className="min-w-0 py-0.5 leading-snug">{message}</span>
            <button
                type="button"
                aria-label="Dispensar aviso"
                onClick={onDismiss}
                className="shrink-0 rounded-full p-1.5 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
                <X className="h-4 w-4" aria-hidden="true" />
            </button>
        </Pill>
    );
}

/**
 * `className` reposiciona a pilha (no celular do convidado ela desce para não cobrir a prévia da câmera);
 * `compact` é a janela pequena.
 */
export function RoomStatusStack({ presenter, compact = false, className }) {
    const { sharing, error: shareError, stop, clearError: clearShareError, pending } = useScreenShare();
    const { error: backgroundError, clearError: clearBackgroundError } = useBackgroundEffect();
    // Erro de gravar ou parar a gravação (cota, plano, servidor de gravação): o atendente precisa saber por que nada aconteceu
    const recordingAgent = useCallRecording()?.agent;
    const recordingError = recordingAgent?.error ?? null;
    if (!presenter && !sharing && !shareError && !backgroundError && !recordingError) return null;
    return (
        <div className={cn('pointer-events-none absolute inset-x-0 top-3 z-20 flex flex-col items-center gap-2 px-3', className)}>
            {presenter && (
                <Pill role="status" compact={compact} data-screen-share-presenter="" className={compact ? 'pr-3' : 'pr-3.5'}>
                    <MonitorUp className="h-4 w-4 shrink-0 text-cyan-300" aria-hidden="true" />
                    <span className="min-w-0 truncate">{displayNameOf(presenter, 'O contato')} está apresentando a tela</span>
                </Pill>
            )}
            {sharing && (
                <Pill role="status" compact={compact} data-screen-share-self="">
                    <MonitorUp className="h-4 w-4 shrink-0 text-cyan-300" aria-hidden="true" />
                    <span className="min-w-0 truncate">Você está compartilhando a tela</span>
                    <button
                        type="button"
                        disabled={pending}
                        onClick={stop}
                        className="shrink-0 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-60"
                    >
                        Parar
                    </button>
                </Pill>
            )}
            {shareError && <ErrorNotice message={shareError} onDismiss={clearShareError} compact={compact} />}
            {backgroundError && <ErrorNotice message={backgroundError} onDismiss={clearBackgroundError} compact={compact} />}
            {recordingError && <ErrorNotice message={recordingError} onDismiss={recordingAgent.clearError} compact={compact} />}
        </div>
    );
}
